import { Fragment } from 'react';
import { msg } from '@gonfalon/messages/msg';

import { CodeSpan } from '../../CodeSpan';

import styles from './RatioMetricDefinitionBox.module.css';

/**
 * Returns the aggregation label for one leg of a ratio metric definition,
 * e.g. "Count", "Sum", "Average", or "Count Distinct".
 */
export function getRatioAggLabel(unitAggType?: string, isNumeric?: boolean): string {
  if (unitAggType === 'count_distinct') {
    return msg('metrics.definitionSummary.ratioCountDistinct');
  }
  if (isNumeric && unitAggType === 'sum') {
    return msg('metrics.definitionSummary.sumPrefix');
  }
  if (isNumeric && unitAggType === 'average') {
    return msg('metrics.definitionSummary.averagePrefix');
  }
  return msg('metrics.definitionSummary.countPrefix');
}

export type RatioMetricDefinitionBoxProps = {
  numeratorAggLabel: string;
  numeratorAggColumn?: string;
  denominatorAggLabel: string;
  denominatorAggColumn?: string;
  units: string[];
  id?: string;
};

function TermRow({
  aggLabel,
  aggColumn,
  termLabel,
  units,
}: {
  aggLabel: string;
  aggColumn?: string;
  termLabel: 'numerator' | 'denominator';
  units: string[];
}) {
  return (
    <div className={styles.termRow}>
      <span className={styles.aggLabel}>{aggLabel}</span>
      {aggColumn && (
        <>
          <span>{msg('metrics.definitionSummary.columnOn')}</span>
          <span className={styles.aggLabel}>{aggColumn}</span>
        </>
      )}
      <span>{msg('metrics.definitionSummary.termEventsPer', { term: termLabel })}</span>
      {units.length === 0 ? (
        <CodeSpan textValue={msg('metrics.definitionSummary.analysisUnitPlaceholder')} />
      ) : (
        units.map((unit, i) => (
          <Fragment key={unit}>
            {i > 0 && <span>{i === units.length - 1 ? msg('metrics.definitionSummary.unitsOr') : ','}</span>}
            <CodeSpan textValue={unit} />
          </Fragment>
        ))
      )}
    </div>
  );
}

export function RatioMetricDefinitionBox({
  numeratorAggLabel,
  numeratorAggColumn,
  denominatorAggLabel,
  denominatorAggColumn,
  units,
  id,
}: RatioMetricDefinitionBoxProps) {
  return (
    <div className={styles.definitionBox} id={id}>
      <div className={styles.definitionContent}>
        <TermRow aggLabel={numeratorAggLabel} aggColumn={numeratorAggColumn} termLabel="numerator" units={units} />
        <div className={styles.definitionDivider} />
        <TermRow
          aggLabel={denominatorAggLabel}
          aggColumn={denominatorAggColumn}
          termLabel="denominator"
          units={units}
        />
      </div>
    </div>
  );
}
